import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";

import { Button, Nav, Dropdown, Modal, Spinner } from "react-bootstrap";
import {
  NavLink,
  Link,
  useNavigate,
  useLocation,
  Outlet,
} from "react-router-dom";

import { toast } from "react-toastify";
import {
  TUTOR_ONBOARDING_API,
  NOTIFICATION_API,
  STUDENT_SIDEBAR_LOCK_API,
} from "../../../api/api";

import { FiMenu, FiX } from "react-icons/fi";
import { FaChevronDown } from "react-icons/fa";

// import assignment from "../../../assets/student/student-assignment-img.png";
// import overview from "../../../assets/student/student-over-img.png";
// import courses from "../../../assets/student/student-courses-img.png";
// import internship from "../../../assets/student/student-internship-img.png";
// import certificate from "../../../assets/student/student-certificate-img.png";
// import doubts from "../../../assets/student/student-chat-img.png";
// import notification from "../../../assets/notification-icon.png";

import Logo from "../../../assets/Skilledition-logo.png";
// import upgradeImg from "../../../assets/student/student-upgrade-img.png";

// latesticons
import overview from "../../../assets/icons/student/overview.svg";
import course from "../../../assets/icons/student/course.svg";
import assignment from "../../../assets/icons/student/assignment.svg";
import certificate from "../../../assets/icons/student/certificate.svg";
import internship from "../../../assets/icons/student/internship.svg";
import chat from "../../../assets/icons/student/chat.svg";
import notificationIcon from "../../../assets/icons/student/notification.svg";
import lock from "../../../assets/icons/student/lock.svg";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentSidebar.css";

const notificationRouteMap = {
  chat: "/student/chats",
  certificate: "/student/certificate",
  internship: "/student/internship",
};

const StudentSidebar = () => {
  const [userData, setUserData] = useState({
    full_name: "",
    role: "",
    student_reg_number: "",
    profile_image: "",
  });

  const [accessData, setAccessData] = useState({
    overview_unlocked: false,
    assignment_unlocked: false,
    certificate_unlocked: false,
    internship_unlocked: false,
    chat_unlocked: false,
  });

  const [open, setOpen] = useState(false);

  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState("unread");
  const [notifications, setNotifications] = useState([]);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  //loading
  const [isUserLoaded, setIsUserLoaded] = useState(false);
  const [loadingNotification, setLoadingNotification] = useState(true);
  const [loadingAccessData, setLoadingAccessData] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      return;
    }

    const fetchUser = async () => {
      try {
        const response = await axios.post(
          TUTOR_ONBOARDING_API.TUTOR_USER_ROUTES_GET_USER,
          {
            user_id: userId,
          },
        );

        const data = response.data;
        if (data.statusCode === 200) {
          setUserData(data.user || {});
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        // Server responded with non-2xx status (like 400, 500)
        if (error.response?.data?.message) {
          showErrorToast(error.response.data.message);
        } else {
          showErrorToast("Network error! Please try again.");
        }
      } finally {
        setIsUserLoaded(true);
      }
    };

    fetchUser();
  }, []);

  // Define role once
  const role = userData.role?.toLowerCase() || "";

  useEffect(() => {
    if (isUserLoaded && role !== "student") {
      navigate("/signin");
    }
  }, [isUserLoaded, role, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingAccessData(false);
      navigate("/signin");

      return;
    }

    const fetchAccessData = async () => {
      try {
        const response = await axios.post(
          STUDENT_SIDEBAR_LOCK_API.STUDENT_DASHBOARD_LOCK,
          { student_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setAccessData(data.data || {});
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetch access data:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setLoadingAccessData(false);
      }
    };

    fetchAccessData();
  }, [navigate]);

  const fetchNotification = useCallback(async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setLoadingNotification(false);

      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.GET_NOTIFICATION,
        { receiver_id: userId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        setNotifications(data.notification_data || []);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error fetch notification:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setLoadingNotification(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotification();
  }, [fetchNotification]);

  const handleMarkAsRead = async (notificationId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.MARK_NOTIFICATION_AS_READ,
        { notification_id: notificationId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        await fetchNotification();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating Mark as read:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const handleRemoveNotification = async (notificationId) => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/signin");

      return;
    }

    try {
      const response = await axios.post(
        NOTIFICATION_API.DELETE_NOTIFICATION,
        { notification_id: notificationId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        // toast.success(data.message, {
        //   position: "top-right",
        //   autoClose: 1500,
        //   theme: "colored",
        // });
        await fetchNotification();
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error Delete Notification:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    }
  };

  const handleNotificationClick = async (notification) => {
    const { notification_id, type, is_read } = notification;

    try {
      // Mark as read (only if unread)
      if (!is_read) {
        await handleMarkAsRead(notification_id);
      }

      // Navigate based on type
      const route = notificationRouteMap[type];

      if (route) {
        navigate(route);
      }
      setShowNotifications(false);
    } catch (error) {
      console.error("Notification click error:", error);
    }
  };

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications],
  );

  const readCount = useMemo(
    () => notifications.filter((n) => n.is_read).length,
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    if (activeTab === "unread") return notifications.filter((n) => !n.is_read);
    if (activeTab === "read") return notifications.filter((n) => n.is_read);
    return notifications;
  }, [notifications, activeTab]);

  // const markAllAsRead = () => {
  //   setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  // };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const handleNavClick = () => {
  //   if (window.innerWidth <= 991) {
  //     setOpen(false);
  //   }
  // };

  //assignement-details section
  const isAssignmentRunning = location.pathname.includes(
    "/student/assignment-detail",
  );

  const handleNavClick = (e, path, isUnlocked) => {
    if (!isUnlocked) {
      e.preventDefault();
      return;
    }

    if (isAssignmentRunning) {
      e.preventDefault();
      setPendingPath(path);
      setShowLeaveModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userRole");

    navigate("/signin");
  };

  return (
    <>
      <div className="student-layout-section">
        {/* Sidebar Toggle for Mobile */}
        <div className="student-sidebar-toggle" onClick={() => setOpen(!open)}>
          {open ? <FiX /> : <FiMenu />}
        </div>

        {/* Sidebar */}
        <div className={`student-sidebar-section ${open ? "open" : ""}`}>
          <div className="student-sidebar-logo">
            <img src={Logo} alt="Skill Edition Logo" />
          </div>

          {loadingAccessData ? (
            <div className="text-center mt-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <Nav className="student-sidebar-nav">
              <NavLink
                to="/student/overview"
                end
                // className={({ isActive }) =>
                //   isActive
                //     ? "student-sidebar-link active"
                //     : "student-sidebar-link"
                // }
                className={({ isActive }) =>
                  `student-sidebar-link ${isActive ? "active" : ""} ${
                    !accessData?.overview_unlocked ? "locked" : ""
                  }`
                }
                onClick={(e) =>
                  handleNavClick(
                    e,
                    "/student/overview",
                    accessData?.overview_unlocked,
                  )
                }
                title={!accessData?.overview_unlocked ? "Locked" : ""}
              >
                <img
                  src={overview}
                  alt="overview"
                  className="student-sidebar-icon"
                />
                <div className="student-sidebar-text">
                  <span>Overview</span>
                  {!accessData.overview_unlocked && (
                    <img
                      src={lock}
                      alt="lock"
                      className="student-sidebar-lock-icon"
                    />
                  )}{" "}
                </div>
              </NavLink>

              <NavLink
                to="/student/courses"
                className="student-sidebar-link"
                onClick={(e) => handleNavClick(e, "/student/courses", true)}
              >
                <img
                  src={course}
                  alt="courses"
                  className="student-sidebar-icon"
                />
                Courses
              </NavLink>

              <NavLink
                to="/student/assignments"
                className={`student-sidebar-link ${
                  !accessData?.assignment_unlocked ? "locked" : ""
                }`}
                onClick={(e) =>
                  handleNavClick(
                    e,
                    "/student/assignments",
                    accessData?.assignment_unlocked,
                  )
                }
                title={!accessData?.assignment_unlocked ? "Locked" : ""}
              >
                <img
                  src={assignment}
                  alt="assignment"
                  className="student-sidebar-icon"
                />
                <div className="student-sidebar-text">
                  <span>Assignments</span>
                  {/* <span className="student-sidebar-badge">4</span> */}
                  {!accessData.assignment_unlocked && (
                    <img
                      src={lock}
                      alt="lock"
                      className="student-sidebar-lock-icon"
                    />
                  )}{" "}
                </div>
              </NavLink>

              <NavLink
                to="/student/chats"
                className={`student-sidebar-link ${
                  !accessData?.chat_unlocked ? "locked" : ""
                }`}
                onClick={(e) =>
                  handleNavClick(e, "/student/chats", accessData?.chat_unlocked)
                }
                title={!accessData?.chat_unlocked ? "Locked" : ""}
              >
                <img src={chat} alt="chats" className="student-sidebar-icon" />
                <div className="student-sidebar-text">
                  <span>Chats</span>
                  {/* <span className="student-sidebar-badge">2</span> */}
                  {!accessData.chat_unlocked && (
                    <img
                      src={lock}
                      alt="lock"
                      className="student-sidebar-lock-icon"
                    />
                  )}
                </div>
              </NavLink>

              <NavLink
                to="/student/certificate"
                className={`student-sidebar-link ${
                  !accessData?.certificate_unlocked ? "locked" : ""
                }`}
                onClick={(e) =>
                  handleNavClick(
                    e,
                    "/student/certificate",
                    accessData?.certificate_unlocked,
                  )
                }
                title={!accessData?.certificate_unlocked ? "Locked" : ""}
              >
                <img
                  src={certificate}
                  alt="certificate"
                  className="student-sidebar-icon"
                />
                <div className="student-sidebar-text">
                  <span>Certificate</span>
                  {!accessData.certificate_unlocked && (
                    <img
                      src={lock}
                      alt="lock"
                      className="student-sidebar-lock-icon"
                    />
                  )}
                </div>
              </NavLink>

              <NavLink
                to="/student/internship"
                className={`student-sidebar-link ${
                  !accessData?.internship_unlocked ? "locked" : ""
                }`}
                onClick={(e) =>
                  handleNavClick(
                    e,
                    "/student/internship",
                    accessData?.internship_unlocked,
                  )
                }
                title={!accessData?.internship_unlocked ? "Locked" : ""}
              >
                <img
                  src={internship}
                  alt="internship"
                  className="student-sidebar-icon"
                />
                <div className="student-sidebar-text">
                  <span>Internship</span>
                  {!accessData.internship_unlocked && (
                    <img
                      src={lock}
                      alt="lock"
                      className="student-sidebar-lock-icon"
                    />
                  )}
                </div>
              </NavLink>
            </Nav>
          )}
          <div className="student-sidebar-upgrade">
            {/* <img src={upgradeImg} alt="" />
            <h6>Upgrade to Premium member</h6>
            <Button className="student-sidebar-upgrade-butnnn">
              Upgrade now
            </Button> */}
            <p>© 2026 Skilledition</p>
          </div>
        </div>

        {/* Main content wrapper */}
        <div className={`student-main-wrapper ${open ? "shifted" : ""}`}>
          {/* Header */}
          <div className="student-slider-header-top">
            <div className="student-slider-right" ref={dropdownRef}>
              <div
                className="student-slider-notification-icon"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <img
                  src={notificationIcon}
                  alt="Notifications"
                  className="student-slider-notification-img"
                />
                {unreadCount > 0 && (
                  <span className="student-slider-badge">{unreadCount}</span>
                )}
              </div>

              {showNotifications && (
                <>
                  <div
                    className="student-slider-notification-overlay"
                    onClick={() => setShowNotifications(false)}
                  />
                  <div className="student-slider-notification-dropdown">
                    <h6>Notifications</h6>
                    <div className="student-slider-tabs">
                      <button
                        className={activeTab === "all" ? "active" : ""}
                        onClick={() => setActiveTab("all")}
                      >
                        All ({notifications.length})
                      </button>
                      <button
                        className={activeTab === "unread" ? "active" : ""}
                        onClick={() => setActiveTab("unread")}
                      >
                        Unread ({unreadCount})
                      </button>
                      <button
                        className={activeTab === "read" ? "active" : ""}
                        onClick={() => setActiveTab("read")}
                      >
                        Read ({readCount})
                      </button>
                    </div>

                    <div className="student-slider-notification-list">
                      {loadingNotification ? (
                        <div className="text-center mt-5">
                          <Spinner animation="border" variant="primary" />
                        </div>
                      ) : filteredNotifications.length > 0 ? (
                        filteredNotifications.map((n) => (
                          <div
                            key={n.notification_id}
                            className={`student-slider-notification-item ${
                              n.is_read ? "read" : "unread"
                            }`}
                            onClick={() => handleNotificationClick(n)}
                          >
                            <div className="student-slider-notification-header">
                              <strong>{n.type || ""}</strong>
                              <button
                                className="student-slider-close-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveNotification(n.notification_id);
                                }}
                              >
                                ×
                              </button>
                            </div>
                            <p>{n.message || ""}</p>

                            {/* <small style={{ fontSize: "12px", color: "#777" }}>
                              {n.sender_name} •{" "}
                              {new Date(n.created_at).toLocaleDateString()}
                            </small> */}

                            {!n.is_read && (
                              <button
                                className="student-slider-okay-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // mark as read + navigate together
                                  handleNotificationClick(n);
                                }}
                              >
                                Okay
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="student-slider-no-notifications">
                          No notifications yet 🎉
                        </p>
                      )}
                    </div>

                    {/* <div
                      className="student-slider-mark-read"
                      onClick={markAllAsRead}
                    >
                      Mark all as read
                    </div> */}
                  </div>
                </>
              )}

              {/* Profile Dropdown */}
              <Dropdown className="student-slider-profile-dropdown" align="end">
                <Dropdown.Toggle
                  className="student-slider-profile-btn"
                  id="dropdown-basic"
                >
                  <div className="student-slider-profile-info">
                    <div className="student-slider-profile-icon">
                      {userData.profile_image ? (
                        <img
                          src={userData.profile_image}
                          alt="profile"
                          className="student-sidebar-profile-img"
                        />
                      ) : (
                        initialsFromName(userData.full_name)
                      )}
                    </div>
                    <div className="student-slider-profile-text">
                      <span
                        className="student-slider-profile-name"
                        title={userData.full_name}
                      >
                        {userData.full_name || "User"}
                      </span>
                      <span className="student-slider-profile-role">
                        {/* {userData.role
                          ? userData.role.charAt(0).toUpperCase() +
                            userData.role.slice(1)
                          : "User"} */}
                        {userData.student_reg_number || ""}
                      </span>
                    </div>
                    <FaChevronDown className="student-slider-dropdown-arrow" />
                  </div>
                </Dropdown.Toggle>

                <Dropdown.Menu className="student-slider-profile-menu">
                  <Dropdown.Item as={Link} to="/student/profile">
                    Profile
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item as={Link} to="/student/purchase">
                    My Purchase
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>

        {/* Outlet renders student pages */}
        <div className="student-layout-content">
          <Outlet />
        </div>
      </div>

      <Modal
        show={showLeaveModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="assignment-sidebar-modal"
      >
        <Modal.Body className="assignment-sidebar-modal-body text-center">
          <h4 className="assignment-sidebar-modal-title">Are you sure?</h4>

          <p className="assignment-sidebar-modal-text">
            You are currently attempting an assignment.
            <br />
            If you leave this page, all your answers will be lost.
            <br />
            Are you sure you want to continue?
          </p>

          <div className="assignment-sidebar-modal-actions d-flex justify-content-center gap-3 mt-4">
            <Button
              className="assignment-sidebar-modal-btn assignment-sidebar-modal-btn-cancel"
              onClick={() => {
                setShowLeaveModal(false);
                setPendingPath(null);
              }}
            >
              Stay Here
            </Button>

            <Button
              className="assignment-sidebar-modal-btn assignment-sidebar-modal-btn-confirm"
              onClick={() => {
                setShowLeaveModal(false);
                navigate(pendingPath);
                setPendingPath(null);
              }}
            >
              Leave Page
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default StudentSidebar;
